import React, { useEffect, useId, useState } from 'react';
import { X, Apple, Flame, AlertCircle, RefreshCw, ZoomIn } from 'lucide-react';
import { fetchFoodLogById } from '../../services/clientApi';
import FoodLogPhoto from './FoodLogPhoto';

export default function FoodLogDrawer({ logId, language, onClose, onImageClick, clientTargets, todayCaloriesSoFar }) {
  const titleId = useId();
  const he = language === 'hebrew';
  const isRTL = he;
  const [log, setLog] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!logId) return undefined;
    let cancelled = false;
    setStatus('loading');
    setLog(null);
    setErrorMessage('');

    fetchFoodLogById(logId).then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        setStatus('error');
        setErrorMessage(error?.message || (he ? 'לא ניתן לטעון את פירוט הארוחה.' : 'Could not load this meal breakdown.'));
        return;
      }
      setLog(data);
      setStatus('success');
    });

    return () => {
      cancelled = true;
    };
  }, [logId, he]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!logId) return null;

  const foodItems = Array.isArray(log?.food_items) ? log.food_items : [];
  const warnings = Array.isArray(log?.dietary_warnings) ? log.dietary_warnings : [];
  const calories = log?.total_calories ?? foodItems.reduce((sum, item) => sum + (item.macros?.calories || 0), 0);
  const protein = log?.total_protein_g ?? foodItems.reduce((sum, item) => sum + (item.macros?.protein_g || 0), 0);
  const carbs = log?.total_carbs_g ?? foodItems.reduce((sum, item) => sum + (item.macros?.carbs_g || 0), 0);
  const fat = log?.total_fat_g ?? foodItems.reduce((sum, item) => sum + (item.macros?.fat_g || 0), 0);

  const reload = () => {
    setStatus('loading');
    fetchFoodLogById(logId).then(({ data, error }) => {
      if (error || !data) {
        setStatus('error');
        setErrorMessage(error?.message || (he ? 'לא ניתן לטעון את פירוט הארוחה.' : 'Could not load this meal breakdown.'));
        return;
      }
      setLog(data);
      setStatus('success');
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex ${isRTL ? 'justify-start' : 'justify-end'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/55 cursor-pointer"
        aria-label={he ? 'סגור פירוט' : 'Close meal detail'}
        onClick={onClose}
      />
      <aside
        data-tour="playbook-chat-food-drawer"
        className={`
          relative h-full w-full max-w-md flex flex-col
          bg-surface dark:bg-surface-dark border-s border-line dark:border-line-dark
          transform transition-transform duration-200 ease-out
        `}
        style={{ animation: isRTL ? 'drawer-from-start 200ms ease-out' : 'drawer-from-end 200ms ease-out' }}
      >
        <header className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-line dark:border-line-dark">
          <div className="flex items-center gap-2 min-w-0">
            <Apple size={18} className="text-forest-ondark flex-shrink-0" aria-hidden="true" />
            <h2 id={titleId} className="text-[15px] font-semibold text-ink dark:text-ink-dark truncate">
              {log?.meal_label || (he ? 'פירוט ארוחה' : 'Meal breakdown')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center rounded-md text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors duration-[120ms] ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark focus-visible:ring-offset-2"
            aria-label={he ? 'סגור' : 'Close'}
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4">
          {status === 'loading' && (
            <div className="space-y-3" aria-busy="true">
              <div className="terminal-skeleton h-40 w-full rounded-lg" />
              <div className="terminal-skeleton h-16 w-full rounded-lg" />
              <div className="terminal-skeleton h-24 w-full rounded-lg" />
            </div>
          )}

          {status === 'error' && (
            <div className="rounded-lg border border-line dark:border-line-dark p-4 space-y-3">
              <p className="text-[13px] text-ink dark:text-ink-dark">{errorMessage}</p>
              <button
                type="button"
                onClick={reload}
                className="min-h-[44px] px-4 rounded-md bg-forest-ondark text-white font-semibold text-[13px] inline-flex items-center gap-2 hover:opacity-90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark focus-visible:ring-offset-2"
              >
                <RefreshCw size={16} />
                {he ? 'נסה שוב' : 'Try again'}
              </button>
            </div>
          )}

          {status === 'success' && log && (
            <div className="space-y-6">
              {(log.has_image || log.image_url) ? (
                <div className="w-full relative group">
                  <FoodLogPhoto
                    log={log}
                    alt={log.meal_label || 'Meal photo'}
                    language={language}
                    className="w-full h-48 object-cover rounded-lg border border-line dark:border-line-dark"
                    onClick={(src) => onImageClick && onImageClick(src)}
                  />
                  <div className="absolute inset-0 pointer-events-none bg-black/0 group-hover:bg-black/5 transition-colors rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 bg-black/50 text-white p-2 rounded-full backdrop-blur-md transition-opacity">
                      <ZoomIn size={20} />
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-4 gap-2">
                <MacroTile label={he ? 'קק״ל' : 'kcal'} value={Math.round(calories)} tone="ink" target={clientTargets?.calories} todaySoFar={todayCaloriesSoFar} he={he} />
                <MacroTile label={he ? 'חלבון' : 'Protein'} value={`${Math.round(protein)}g`} tone="protein" target={clientTargets?.protein} he={he} />
                <MacroTile label={he ? 'פחמימות' : 'Carbs'} value={`${Math.round(carbs)}g`} tone="carbs" target={clientTargets?.carbs} he={he} />
                <MacroTile label={he ? 'שומן' : 'Fat'} value={`${Math.round(fat)}g`} tone="fat" target={clientTargets?.fats} he={he} />
              </div>

              {(log.plan_score != null || log.health_score != null) && (
                <div className="flex gap-4">
                  {log.plan_score != null && (
                    <ScoreRow label={he ? 'ציון תוכנית' : 'Plan score'} value={log.plan_score} reason={log.plan_score_reason} barClass="ind-carbs-fill" />
                  )}
                  {log.health_score != null && (
                    <ScoreRow label={he ? 'ציון בריאות' : 'Health score'} value={log.health_score} reason={log.health_score_reason} barClass="ind-protein-fill" />
                  )}
                </div>
              )}

              {warnings.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-line dark:border-line-dark p-3">
                  <AlertCircle size={16} className="text-[color:var(--warning)] mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p className="text-[13px] text-ink dark:text-ink-dark">{warnings.join(', ')}</p>
                </div>
              )}

              <div>
                <h3 className="text-[13px] font-semibold text-ink dark:text-ink-dark mb-2">
                  {he ? 'פריטים' : 'Items'} ({foodItems.length})
                </h3>
                {foodItems.length === 0 ? (
                  <p className="text-[13px] text-ink-muted dark:text-ink-muted-dark">
                    {he ? 'אין פירוט פריטים לארוחה זו.' : 'No item breakdown for this meal.'}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {foodItems.map((item, idx) => (
                      <li key={idx} className="rounded-lg border border-line dark:border-line-dark p-3">
                        <div className="flex justify-between gap-2 mb-1">
                          <span className="text-[13px] font-medium text-ink dark:text-ink-dark">{item.name || (he ? 'פריט' : 'Item')}</span>
                          {item.portion_estimate ? (
                            <span className="text-[11px] text-ink-muted dark:text-ink-muted-dark">{item.portion_estimate}</span>
                          ) : null}
                        </div>
                        <div className="flex gap-3 text-[11px] tabular-nums">
                          <span className="text-ink dark:text-ink-dark">{Math.round(item.macros?.calories || 0)} kcal</span>
                          <span className="ind-protein">{Math.round(item.macros?.protein_g || 0)}g P</span>
                          <span className="ind-carbs">{Math.round(item.macros?.carbs_g || 0)}g C</span>
                          <span className="ind-fat">{Math.round(item.macros?.fat_g || 0)}g F</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function MacroTile({ label, value, tone, target, todaySoFar, he }) {
  const toneClass =
    tone === 'protein' ? 'ind-protein' :
    tone === 'carbs' ? 'ind-carbs' :
    tone === 'fat' ? 'ind-fat' :
    'text-ink dark:text-ink-dark';
  return (
    <div className="rounded-lg border border-line dark:border-line-dark p-2 text-center flex flex-col justify-between h-full">
      <div>
        {tone === 'ink' ? (
          <Flame size={12} className="mx-auto mb-1 text-ink dark:text-ink-dark" aria-hidden="true" />
        ) : (
          <div className="h-3 mb-1" aria-hidden="true" />
        )}
        <div className={`text-[13px] font-medium tabular-nums ${toneClass}`}>{value}</div>
        <div className="text-[11px] text-ink-muted dark:text-ink-muted-dark">{label}</div>
      </div>
      {target && tone === 'ink' ? (
        <div className="text-[10px] text-ink-muted dark:text-ink-muted-dark mt-2 leading-tight tabular-nums pt-2 border-t border-line dark:border-line-dark border-opacity-50">
          <span className={todaySoFar > target ? 'text-[color:var(--danger-text)]' : ''}>
            {todaySoFar}
          </span> / {target}
          <div className="text-[9px] mt-0.5">{he ? 'עד כה' : 'so far'}</div>
        </div>
      ) : target ? (
        <div className="text-[10px] text-ink-muted dark:text-ink-muted-dark mt-2 leading-tight tabular-nums pt-2 border-t border-line dark:border-line-dark border-opacity-50">
          {target} {he ? 'יעד' : 'target'}
        </div>
      ) : null}
    </div>
  );
}

function ScoreRow({ label, value, reason, barClass }) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-ink-muted dark:text-ink-muted-dark">{label}</span>
        <span className="font-medium text-ink dark:text-ink-dark tabular-nums">{value}/10</span>
      </div>
      <div className="h-1.5 rounded-full bg-moss dark:bg-moss-dark overflow-hidden shrink-0">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${Math.min(100, (Number(value) / 10) * 100)}%` }} />
      </div>
      {reason && (
        <p className="text-[11px] text-ink-muted dark:text-ink-muted-dark leading-snug mt-1.5 line-clamp-3" title={reason}>
          {reason}
        </p>
      )}
    </div>
  );
}
